import { Image, Link, X, FolderOpen, Database, FileText, Video, Upload, Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FileUpload, { FilePreview, useFileUpload } from '../../components/FileUpload';
import { 
  uploadFile, 
  deleteFile, 
  listFiles, 
  generateFilePath, 
  validateFile, 
  formatFileSize,
  getFileTypeCategory,
  batchUploadFiles,
  getStorageUsage
} from '../../firebase/r2storage';
import { 
  saveResource, 
  updateResource, 
  deleteResource, 
  getResources,
  subscribeToResources 
} from '../../firebase/collections';
import BookmarkButton from '../../components/BookmarkButton';
import { formatTimestamp } from '../../utils/timestampUtils';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';

const ResourceManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  
  // Check if user is a super admin (not coaching admin)
  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);
  
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [viewingResource, setViewingResource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCoachingName, setFilterCoachingName] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [storageUsage, setStorageUsage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const {
    uploadedFiles: hookUploadedFiles,
    uploading: hookUploading,
    progress: hookProgress,
    uploadFile: hookUploadFile,
    removeFile: hookRemoveFile,
    setUploadedFiles: setHookUploadedFiles
  } = useFileUpload({ storagePath: 'resources' });

  // Load resources
  useEffect(() => {
    loadResources();
    loadStorageUsage();
  }, [coachingId]);

  // Subscribe to real-time updates
  useEffect(() => {
    const filters = coachingId ? { coachingId: coachingId } : {};
    const unsubscribe = subscribeToResources((result) => {
      if (result.success && Array.isArray(result.data)) {
        let resourcesData = result.data || [];
        
        // For super admins: filter out institute resources (resources with coachingId)
        if (isSuperAdmin) {
          resourcesData = resourcesData.filter(resource => !resource.coachingId);
        }
        
        // Resources are already filtered by coachingId in the query (if provided)
        setResources(resourcesData);
      } else {
        }
    }, filters);

    return () => unsubscribe();
  }, [coachingId, isSuperAdmin]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const result = await getResources(coachingId || {});
      if (result.success && Array.isArray(result.data)) {
        let resourcesData = result.data;
        
        // For super admins: filter out institute resources (resources with coachingId)
        if (isSuperAdmin) {
          resourcesData = resourcesData.filter(resource => !resource.coachingId);
        }
        
        setResources(resourcesData);
        
      } else {
        setResources([]);
      }
    } catch (error) {
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStorageUsage = async () => {
    try {
      const usage = await getStorageUsage();
      if (usage.success) {
        setStorageUsage(usage);
      }
    } catch (error) {
      }
  };

  // Handle file upload
  const handleFilesUploaded = useCallback((uploadedFiles) => {
    setUploadedFiles(prev => [...prev, ...uploadedFiles]);
    setSelectedFiles([]);
  }, []);

  const handleFileRemoved = useCallback((file) => {
    setSelectedFiles(prev => prev.filter(f => f !== file));
  }, []);

  // Add new resource
  const handleAddResource = async (resourceData) => {
    try {
      // Determine coachingId value - must be string or null, never undefined
      const finalCoachingId = (coachingId && isCoachingAdmin) 
        ? coachingId 
        : null; // Super admins get null, not undefined
      
      // Remove coachingId from resourceData to avoid undefined values
      const { coachingId: _, ...resourceDataWithoutCoachingId } = resourceData;
      
      const resourceWithFiles = {
        ...resourceDataWithoutCoachingId,
        files: uploadedFiles.map(file => ({
          name: file.metadata.name,
          url: file.url,
          path: file.path,
          size: file.metadata.size,
          type: file.metadata.type
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Only add coachingId if it has a valid value (not undefined)
      if (finalCoachingId !== undefined && finalCoachingId !== null) {
        resourceWithFiles.coachingId = finalCoachingId;
      } else if (isSuperAdmin) {
        // Explicitly set to null for super admins to avoid undefined
        resourceWithFiles.coachingId = null;
        // Admin resources are always public
        resourceWithFiles.isPublic = true;
      }

      const result = await saveResource(resourceWithFiles);
      if (result.success) {
        setShowAddModal(false);
        setUploadedFiles([]);
        setSelectedFiles([]);
        // Reload resources after a short delay to ensure data is saved
        setTimeout(() => {
          loadResources();
        }, 500);
      }
    } catch (error) {
    }
  };

  // Update resource
  const handleUpdateResource = async (resourceId, updatedData) => {
    try {
      // Determine coachingId value - must be string or null, never undefined
      const finalCoachingId = (coachingId && isCoachingAdmin) 
        ? coachingId 
        : null; // Super admins get null, not undefined
      
      // Remove coachingId from updatedData to avoid undefined values
      const { coachingId: _, ...updatedDataWithoutCoachingId } = updatedData;
      
      const dataToUpdate = {
        ...updatedDataWithoutCoachingId,
        updatedAt: new Date().toISOString()
      };
      
      // Only add coachingId if it has a valid value (not undefined)
      if (finalCoachingId !== undefined && finalCoachingId !== null) {
        dataToUpdate.coachingId = finalCoachingId;
      } else if (isSuperAdmin) {
        // Explicitly set to null for super admins to avoid undefined
        dataToUpdate.coachingId = null;
      }
      
      const result = await updateResource(resourceId, dataToUpdate);
      if (result.success) {
        setEditingResource(null);
        // Reload resources after a short delay to ensure data is saved
        setTimeout(() => {
          loadResources();
        }, 500);
      }
    } catch (error) {
    }
  };

  // Delete resource
  const handleDeleteResource = async (resourceId) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        setLoading(true);
        const result = await deleteResource(resourceId);
        if (result.success) {
          // Resources will be updated via real-time subscription
          } else {
          }
      } catch (error) {
        } finally {
        setLoading(false);
      }
    }
  };

  // Delete file from storage
  const handleDeleteFile = async (filePath) => {
    try {
      const result = await deleteFile(filePath);
      if (result.success) {
        setUploadedFiles(prev => prev.filter(file => file.path !== filePath));
      }
    } catch (error) {
      }
  };

  // Get unique coaching names from resources
  const uniqueCoachingNames = Array.isArray(resources) 
    ? [...new Set(resources.map(r => r.coachingName).filter(Boolean))].sort()
    : [];

  // Filter and sort resources
  const filteredResources = Array.isArray(resources) ? resources
    .filter(resource => {
      const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           resource.category.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || resource.type === filterType;
      
      const matchesCoachingName = filterCoachingName === 'all' || 
                                   resource.coachingName === filterCoachingName;
      
      return matchesSearch && matchesType && matchesCoachingName;
    })
    .sort((a, b) => {
      // Prioritize admin resources (coachingId is null/undefined) over coaching resources
      const aIsAdmin = !a.coachingId;
      const bIsAdmin = !b.coachingId;
      
      if (aIsAdmin && !bIsAdmin) return -1; // Admin resources first
      if (!aIsAdmin && bIsAdmin) return 1;  // Coaching resources after admin
      
      // If both are same type, sort by the selected criteria
      let comparison = 0;
      
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'updatedAt':
          comparison = new Date(a.updatedAt) - new Date(b.updatedAt);
          break;

        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    }) : [];

  const getResourceIcon = (type) => {
    switch (type) {
      case 'document': return <FileText className="w-6 h-6" />;
      case 'video': return <Video className="w-6 h-6" />;
      case 'image': return <Image className="w-6 h-6" />;
      case 'link': return <Link className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const getResourceTypeColor = (type) => {
    switch (type) {
      case 'document': return 'bg-blue-100 text-blue-800';
      case 'video': return 'bg-purple-100 text-purple-800';
      case 'image': return 'bg-green-100 text-green-800';
      case 'link': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
                         <div>
               <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Resource Management</h1>
               <p className="text-gray-600 dark:text-gray-400">
                 Manage educational resources and files 
                 <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                   {resources.length} resources loaded
                 </span>
               </p>
             </div>
            
                         <div className="flex items-center space-x-3">
               {storageUsage && (
                 <div className="text-sm text-gray-600 dark:text-gray-400">
                   Storage: {storageUsage.formattedSize} ({storageUsage.fileCount} files)
                 </div>
               )}
               
               <button
                 onClick={loadResources}
                 className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
               >
                 <Database className="w-4 h-4 mr-2" />
                 Refresh
               </button>
               
               <button
                 onClick={() => setShowUploadModal(true)}
                 className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
               >
                 <Upload className="w-4 h-4 mr-2" />
                 Bulk Upload
               </button>
               
               <button
                 onClick={() => setShowAddModal(true)}
                 className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
               >
                 <Plus className="w-4 h-4 mr-2" />
                 Add Resource
               </button>
             </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={filterCoachingName}
                onChange={(e) => setFilterCoachingName(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Categories</option>
                {uniqueCoachingNames.map((coachingName) => (
                  <option key={coachingName} value={coachingName}>
                    {coachingName}
                  </option>
                ))}
              </select>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Types</option>
                <option value="document">Documents</option>
                <option value="video">Videos</option>
                <option value="image">Images</option>
                <option value="link">Links</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="createdAt">Date Created</option>
                <option value="updatedAt">Date Updated</option>
                <option value="title">Title</option>

              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredResources.map((resource) => (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Resource Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg ${getResourceTypeColor(resource.type)}`}>
                        {getResourceIcon(resource.type)}
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setEditingResource(resource)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteResource(resource.id)}
                          className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {resource.title}
                    </h3>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                      {resource.description}
                    </p>

                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span className="capitalize">{resource.category}</span>

                    </div>
                  </div>

                  {/* Files List */}
                  {resource.files && resource.files.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Files ({resource.files.length})
                      </h4>
                      <div className="space-y-2">
                        {resource.files.slice(0, 3).map((file, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600 dark:text-gray-400 truncate">
                                {file.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </span>

                            </div>
                          </div>
                        ))}
                        {resource.files.length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{resource.files.length - 3} more files
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(resource.createdAt, { includeTime: false, short: true })}
                      </span>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setViewingResource(resource)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </button>
                        <BookmarkButton
                          itemId={resource.id}
                          type="resource"
                          title={resource.title}
                          description={resource.description}
                          url={resource.url || resource.files?.[0]?.url}
                          thumbnail={resource.thumbnail}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {!loading && filteredResources.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No resources found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || filterType !== 'all' || filterCoachingName !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'Get started by adding your first resource'
              }
            </p>
          </div>
        )}
      </div>

      {/* Add Resource Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Resource</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <AddResourceForm
              onSubmit={handleAddResource}
              onCancel={() => setShowAddModal(false)}
              uploadedFiles={uploadedFiles}
              onFilesUploaded={handleFilesUploaded}
              onFileRemoved={handleFileRemoved}
              defaultCoachingName={isSuperAdmin ? 'Mahstry' : (userProfile?.coachingName || '')}
            />
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Bulk File Upload</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <FileUpload
              onFilesUploaded={handleFilesUploaded}
              onFileRemoved={handleFileRemoved}
              multiple={true}
              maxFiles={50}
              maxSize={100 * 1024 * 1024} // 100MB
              storagePath="resources/bulk"
              showPreview={true}
              showProgress={true}
              showActions={true}
              placeholder="Drag and drop files here for bulk upload"
              allowedTypes={[
                'image/*', 
                'video/*', 
                'application/pdf', 
                'text/*', 
                'application/*',
                'audio/*'
              ]}
            />

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  loadStorageUsage();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Resource Modal */}
      {editingResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Resource</h2>
              <button
                onClick={() => setEditingResource(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <EditResourceForm
              resource={editingResource}
              onSubmit={(updatedData) => handleUpdateResource(editingResource.id, updatedData)}
              onCancel={() => setEditingResource(null)}
              onDeleteFile={handleDeleteFile}
            />
          </div>
        </div>
      )}

      {/* Resource Viewer Modal */}
      {viewingResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{viewingResource.title}</h2>
              <button
                onClick={() => setViewingResource(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Resource Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h3>
                <p className="text-gray-600 dark:text-gray-400">{viewingResource.description || 'No description provided'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</h3>
                  <p className="text-gray-600 dark:text-gray-400">{viewingResource.category || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type</h3>
                  <p className="text-gray-600 dark:text-gray-400 capitalize">{viewingResource.type || 'N/A'}</p>
                </div>
                {viewingResource.coachingName && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Coaching Name</h3>
                    <p className="text-gray-600 dark:text-gray-400">{viewingResource.coachingName}</p>
                  </div>
                )}
              </div>

              {/* Files Display */}
              {viewingResource.files && viewingResource.files.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Files ({viewingResource.files.length})
                  </h3>
                  <div className="space-y-4">
                    {viewingResource.files.map((file, index) => {
                      const isPDF = file.type === 'application/pdf';
                      const isImage = file.type?.startsWith('image/');
                      const isVideo = file.type?.startsWith('video/');
                      const fileUrl = file.url;

                      return (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              {isPDF && <FileText className="w-6 h-6 text-red-500" />}
                              {isImage && <Image className="w-6 h-6 text-blue-500" />}
                              {isVideo && <Video className="w-6 h-6 text-purple-500" />}
                              {!isPDF && !isImage && !isVideo && <FileText className="w-6 h-6 text-gray-500" />}
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                                {file.size && (
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                )}
                              </div>
                            </div>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Open
                            </a>
                          </div>

                          {/* Preview */}
                          {fileUrl && (
                            <div className="mt-3">
                              {isPDF && (
                                <div>
                                  <iframe
                                    src={fileUrl}
                                    className="w-full h-96 border border-gray-200 dark:border-gray-700 rounded"
                                    title={file.name}
                                  />
                                  <p className="text-xs text-gray-500 mt-2 text-center">
                                    If the PDF doesn't load, click "Open" to view in a new tab.
                                  </p>
                                </div>
                              )}
                              {isImage && (
                                <img
                                  src={fileUrl}
                                  alt={file.name}
                                  className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                                  onError={(e) => {
                                    e.target.alt = 'Image failed to load. Click "Open" to view in a new tab.';
                                    e.target.style.border = '2px dashed #ccc';
                                  }}
                                />
                              )}
                              {isVideo && (
                                <video
                                  src={fileUrl}
                                  controls
                                  className="w-full rounded border border-gray-200 dark:border-gray-700"
                                >
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : viewingResource.url ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Resource Link</h3>
                  <a
                    href={viewingResource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline break-all"
                  >
                    {viewingResource.url}
                  </a>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No files or links available for this resource</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add Resource Form Component
const AddResourceForm = ({ onSubmit, onCancel, uploadedFiles, onFilesUploaded, onFileRemoved, defaultCoachingName = '' }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    type: 'document',
    tags: '',
    isPublic: false, // Changed to false by default (private)
    coachingName: defaultCoachingName
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category *
          </label>
          <input
            type="text"
            required
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description *
        </label>
        <textarea
          required
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Coaching Name *
        </label>
        <input
          type="text"
          required
          value={formData.coachingName}
          onChange={(e) => setFormData(prev => ({ ...prev, coachingName: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Enter coaching name"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Type
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="document">Document</option>
            <option value="video">Video</option>
            <option value="image">Image</option>
            <option value="link">Link</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tags
          </label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
            placeholder="Comma-separated tags"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isPublic}
            onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Public resource</span>
        </label>
      </div>

      {/* File Upload Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Upload Files
        </label>
        <FileUpload
          onFilesUploaded={onFilesUploaded}
          onFileRemoved={onFileRemoved}
          multiple={true}
          maxFiles={10}
          maxSize={50 * 1024 * 1024} // 50MB
          storagePath="resources"
          showPreview={true}
          showProgress={true}
          showActions={true}
          placeholder="Drag and drop files here, or click to select"
        />
      </div>

      {/* Uploaded Files Preview */}
      {uploadedFiles.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Uploaded Files ({uploadedFiles.length})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {uploadedFiles.map((file, index) => (
              <FilePreview
                key={index}
                file={file}
                onDelete={() => onFileRemoved(file)}
                showActions={true}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Add Resource
        </button>
      </div>
    </form>
  );
};

// Edit Resource Form Component
const EditResourceForm = ({ resource, onSubmit, onCancel, onDeleteFile }) => {
  const [formData, setFormData] = useState({
    title: resource.title || '',
    description: resource.description || '',
    category: resource.category || '',
    type: resource.type || 'document',
    tags: resource.tags || '',
    isPublic: resource.isPublic !== false,
    coachingName: resource.coachingName || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Title *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Category *
          </label>
          <input
            type="text"
            required
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description *
        </label>
        <textarea
          required
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Coaching Name *
        </label>
        <input
          type="text"
          required
          value={formData.coachingName}
          onChange={(e) => setFormData(prev => ({ ...prev, coachingName: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Enter coaching name"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Type
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="document">Document</option>
            <option value="video">Video</option>
            <option value="image">Image</option>
            <option value="link">Link</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tags
          </label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
            placeholder="Comma-separated tags"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isPublic}
            onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Public resource</span>
        </label>
      </div>

      {/* Existing Files */}
      {resource.files && resource.files.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Existing Files ({resource.files.length})
          </h4>
          <div className="space-y-2">
            {resource.files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">

                  <button
                    onClick={() => onDeleteFile(file.path)}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Update Resource
        </button>
      </div>
    </form>
  );
};

export default ResourceManagement;
